/**
 * Server time for the Light Board timers (SF-04). Every board response
 * carries `serverNow`; the offset to the local clock is measured from the
 * request's round trip, so a workstation clock that runs fast or slow never
 * shows a wrong wait. One shared 1 s ticker drives every timer on screen
 * (Pulse Status gave each card its own interval).
 */
import { clockOffset } from '@velora/domain/lightboard';
import { useSyncExternalStore } from 'react';

let offsetMs = 0;
let tick = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

/** Records a measurement: `serverNow` stamped between `sentAt` and `receivedAt` (local ms). */
export function observeServerTime(serverNow: string, sentAt: number, receivedAt: number): void {
  const server = Date.parse(serverNow);
  if (Number.isNaN(server)) return;
  offsetMs = clockOffset(server, sentAt, receivedAt);
}

export function serverOffsetMs(): number {
  return offsetMs;
}

/** Server time now, in ms. */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  if (!timer) {
    timer = setInterval(() => {
      tick = Date.now();
      for (const l of listeners) l();
    }, 1000);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Re-renders every second with the current server time (ms). */
export function useServerNow(): number {
  const local = useSyncExternalStore(
    subscribe,
    () => tick,
    () => tick,
  );
  return local + offsetMs;
}

/** Test hook. */
export function resetClockForTests(offset = 0): void {
  offsetMs = offset;
  tick = Date.now();
}
